/**
 * Polite live region (Design Bible 9.4).
 *
 * One region for the whole shell, so a screen reader hears route changes, cart
 * changes, and command results in the order they happened. Errors and stops do
 * not go through here — they use `role="alert"` at the place they concern, and
 * they also render inline and persist, because a transient announcement is
 * never the only place an important message appears.
 *
 * Announcements are throttled to one per 10 seconds *for repeated identical
 * text*, which is what stops a reservation countdown from flooding a screen
 * reader while leaving a genuine change immediate.
 */

import { type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const AnnouncerContext = createContext<(message: string) => void>(() => undefined);

const REPEAT_THROTTLE_MS = 10_000;

export function AnnouncerProvider({ children }: { children: ReactNode }): ReactNode {
  const [message, setMessage] = useState("");
  const last = useRef<{ text: string; at: number }>({ text: "", at: 0 });

  const announce = useCallback((next: string) => {
    const now = Date.now();
    if (next === last.current.text && now - last.current.at < REPEAT_THROTTLE_MS) return;
    last.current = { text: next, at: now };
    // Re-announcing an identical string requires a change, so it is cleared
    // first. Without this a repeated result is silent.
    setMessage("");
    setTimeout(() => {
      setMessage(next);
    }, 0);
  }, []);

  const value = useMemo(() => announce, [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <p aria-atomic="true" aria-live="polite" className="u-visually-hidden" role="status">
        {message}
      </p>
    </AnnouncerContext.Provider>
  );
}

export function useAnnounce(): (message: string) => void {
  return useContext(AnnouncerContext);
}
